/**
 * Project config (projects.config JSONB) — shared fetch + query hook.
 *
 * The admin permissions tab writes this object; the records feature reads the
 * deletion-policy keys to gate its UI. One query key, one shape, no drift.
 */
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase/client'

export interface ProjectConfig {
  review_mode?: 'required' | 'none'
  allow_record_assignment?: boolean
  allow_edit_others_records?: boolean
  edit_after_approval?: boolean
  delete_record_roles?: string[]
  allow_delete_submitted_records?: boolean
  allow_delete_others_records?: boolean
  allow_comment_add_all?: boolean
  export_enabled?: boolean
  public_links_enabled?: boolean
  record_id_prefix?: string
}

export async function fetchProjectConfig(projectId: string): Promise<ProjectConfig> {
  const { data, error } = await supabase
    .from('projects')
    .select('config')
    .eq('id', projectId)
    .single()
  if (error) throw error
  return (data?.config as ProjectConfig) ?? {}
}

export function useProjectConfigQuery(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-config', projectId],
    queryFn: () => fetchProjectConfig(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  })
}
